/**
 * AIFeedbackButtons — compact 👍 / 👎 pair for AI-generated suggestions.
 *
 * Records recruiter reactions to any AI output (red flags, summaries, urgency
 * tasks, skill matches, etc.) via POST /api/ai-feedback. Once the recruiter
 * has voted the buttons switch to a confirmed state and an optional short
 * reason box appears on 👎 to capture *why* the AI got it wrong.
 *
 * Kept intentionally tiny (~26px tall) so it can nest inside hover cards and
 * red-flag cards without breaking their compact layout.
 */
import { useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LABEL_BY_KIND = {
    red_flag: "red flag",
    ai_summary: "summary",
    skill_match: "match",
    urgency_task: "task",
    suggestion: "suggestion",
};

export default function AIFeedbackButtons({
    feature,
    targetType,
    targetId,
    payload = null,
    className = "",
    compact = false,
}) {
    const [vote, setVote] = useState(null); // "up" | "down" | null
    const [reason, setReason] = useState("");
    const [sending, setSending] = useState(false);
    const [reasonSent, setReasonSent] = useState(false);

    const label = LABEL_BY_KIND[feature] || "AI output";

    const cast = async (action) => {
        setSending(true);
        try {
            await api.post("/ai-feedback", {
                feature, action, target_type: targetType,
                target_id: String(targetId), payload,
            });
            setVote(action === "accept" ? "up" : "down");
        } catch { toast.error("Couldn't record feedback"); }
        finally { setSending(false); }
    };

    const openDown = () => {
        // 👎 flow: don't POST yet — open the reason box first so we only
        // record ONE row per thumb-down (with reason attached). Prevents
        // the double-count bug where a thumb-down would fire an empty
        // false_positive event followed by a second one with the reason.
        setVote("down");
    };

    const sendReason = async () => {
        setSending(true);
        try {
            await api.post("/ai-feedback", {
                feature, action: "false_positive",
                target_type: targetType, target_id: String(targetId),
                reason: reason.trim().slice(0, 500) || null, payload,
            });
            setReasonSent(true);
            toast.success("Thanks — we'll use this to improve AI accuracy");
        } catch { toast.error("Couldn't send reason"); }
        finally { setSending(false); }
    };

    if (vote === "up") {
        return (
            <div className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 ${className}`}
                data-testid={`ai-feedback-thanks-${feature}`}>
                <Check className="w-3 h-3" /> Thanks
            </div>
        );
    }

    if (vote === "down" && !reasonSent) {
        return (
            <div className={`w-full flex items-center gap-1.5 ${className}`}>
                <input
                    type="text"
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={`Why was this ${label} wrong?`}
                    maxLength={200}
                    className="h-6 px-1.5 text-[11px] border border-border bg-white flex-1 min-w-0"
                    data-testid={`ai-feedback-reason-${feature}`}
                    onKeyDown={(e) => { if (e.key === "Enter") sendReason(); }}
                />
                <button
                    type="button"
                    onClick={sendReason}
                    disabled={sending}
                    className="h-6 px-2 border border-rose-500 text-rose-700 hover:bg-rose-50 text-[10px] font-mono uppercase tracking-widest disabled:opacity-50"
                    data-testid={`ai-feedback-submit-${feature}`}
                >{reason.trim() ? "Send" : "Skip"}</button>
            </div>
        );
    }

    if (vote === "down" && reasonSent) {
        return (
            <div className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-700 ${className}`}
                data-testid={`ai-feedback-logged-${feature}`}>
                <Check className="w-3 h-3" /> Logged
            </div>
        );
    }

    return (
        <div className={`inline-flex items-center gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
            {!compact && (
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mr-1">Was this {label} useful?</span>
            )}
            <button
                type="button"
                title={`This ${label} was helpful`}
                onClick={() => cast("accept")}
                disabled={sending}
                className="h-6 w-6 grid place-items-center border border-border hover:border-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-50"
                data-testid={`ai-feedback-up-${feature}`}
            >
                <ThumbsUp className="w-3 h-3" />
            </button>
            <button
                type="button"
                title={`This ${label} was wrong`}
                onClick={openDown}
                disabled={sending}
                className="h-6 w-6 grid place-items-center border border-border hover:border-rose-500 hover:text-rose-700 transition-colors disabled:opacity-50"
                data-testid={`ai-feedback-down-${feature}`}
            >
                <ThumbsDown className="w-3 h-3" />
            </button>
        </div>
    );
}
